import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Image } from "react-bootstrap";
import {
  Currency,
  GemResponse,
  OnboardingStateResponse,
  StreakStatus,
  WalletList,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";
import { useSetAtom } from "jotai";

import "./DashboardHeroSection.scss";
import GemBalance from "../../GemBalance/GemBalance";
import HyperBalance from "../../HyperBalance/HyperBalance";
import ImageGiftIcon from "../../../../images/gift.svg";
import {
  getStoredHasViewedDashboardExplainer,
  setStoredHasViewedDashboardExplainer,
} from "../../../../utils/storage";
import StreakCount from "../../StreakCount/StreakCount";
import { handleGetMyWallets } from "../../../../api-clients/walletAccountsClient";
import { handleGetGemResponse } from "../../../../api-clients/walletGemsClient";
import { handleGetStreakStatus } from "../../../../api-clients/streaksClient";
import Skeleton from "../../../Skeleton/Skeleton";
import CustomPopover from "../../../CustomPopover/CustomPopover";
import useHprPurchasePlansQuery from "../../../../react-query/queries/use-hpr-purchase-plans";
import {
  handleGetOnboardingState,
  handleUpdateOnboardingState,
} from "../../../../api-clients/userAccountClient";
import useAuth from "../../../../hooks/useAuth";
import useIntersectionObserver from "../../../../hooks/useIntersectionObserver";
import {
  initiateTopupTriggerAtom,
  InitiateTopupTriggers,
} from "../../../../jotai/atoms/analytics";
import PrimaryActionButton from "../../../Buttons/PrimaryActionButton/PrimaryActionButton";
import useOnboardingSteps from "../../../../hooks/useOnboardingSteps";
import { OnboardingSteps, ROUTES } from "../../../../utils/constants";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FEATURE_FLAGS } from "../../../../config/feature-flags";

enum Explainers {
  HPR,
  GEM,
  TILES,
}
const EXPLAINER_DELAY = 3000;
const POPOVER_STEPS = 3;

function LoadingSkeleton() {
  return (
    <div className="d-flex px-3 py-3 gap-2">
      <Skeleton height={28} width={64} />
      <Skeleton height={28} width={64} />
    </div>
  );
}

function DashboardHeroSection() {
  const navigate = useNavigate();
  const { goToStep } = useOnboardingSteps();
  const { t } = useTranslation("dashboard");
  const [activeExplainer, setActiveExplainer] = useState<number>();
  const [walletList, setWalletList] = useState<WalletList>();
  const [gemResponse, setGemResponse] = useState<GemResponse>();
  const [onboardingState, setOnboardingState] =
    useState<OnboardingStateResponse>();
  const { hasWelcomeBonus } = useHprPurchasePlansQuery();
  const [isLoading, setIsLoading] = useState(true);
  const [areBalanceTilesVisible, setAreBalanceTilesVisible] = useState(false);
  const isHprExplainerOpen =
    activeExplainer === Explainers.HPR && areBalanceTilesVisible;
  const isGemExplainerOpen =
    activeExplainer === Explainers.GEM && areBalanceTilesVisible;
  const isTilesExplainerOpen =
    activeExplainer === Explainers.TILES && areBalanceTilesVisible;
  const [streakStatus, setStreakStatus] = useState<StreakStatus>();
  const { isLoggedIn } = useAuth();
  const setInitiateTopupTrigger = useSetAtom(initiateTopupTriggerAtom);
  const isFiatDisplayEnabled = useFeatureFlagEnabled(
    FEATURE_FLAGS.DISPLAY_USER_CURRENCY,
  );
  const walletRecords = walletList?.records;
  const hprWallet =
    walletRecords &&
    walletRecords.find((record) => record.asset === Currency.HPR);
  const mainContentWrapperRef = useRef<HTMLDivElement>(null);

  const handleClaim = () => {
    setInitiateTopupTrigger(InitiateTopupTriggers.DASHBOARD_CLAIM_BONUS);
    goToStep(OnboardingSteps.VIEW_INSURANCE_BONUS);
    navigate(`${ROUTES.ROOT.SIGN_UP}/${ROUTES.ONBOARDING.WELCOME}`);
  };

  const onFlowEnd = useCallback(() => {
    setStoredHasViewedDashboardExplainer(true);
    isLoggedIn && handleUpdateOnboardingState({ dashboardGuide: true });
  }, [isLoggedIn]);

  useIntersectionObserver({
    targetRef: mainContentWrapperRef,
    options: { threshold: 0.9 },
    callback: ([entry]) => setAreBalanceTilesVisible(entry.isIntersecting),
  });

  useEffect(() => {
    (async () => {
      const [wallet, gem, streak, onboarding] = await Promise.all([
        handleGetMyWallets(),
        handleGetGemResponse(),
        handleGetStreakStatus(),
        handleGetOnboardingState(),
      ]);

      setWalletList(wallet);
      setGemResponse(gem);
      setOnboardingState(onboarding);
      if (streak) setStreakStatus(streak);

      setIsLoading(false);
    })();

    if (
      isLoading ||
      isFiatDisplayEnabled ||
      (!isLoggedIn && getStoredHasViewedDashboardExplainer()) ||
      (isLoggedIn && onboardingState?.dashboardGuide)
    ) {
      return;
    }

    const timeoutId = setTimeout(() => {
      isLoggedIn && setActiveExplainer(Explainers.HPR);
    }, EXPLAINER_DELAY);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    isLoading,
    isLoggedIn,
    isFiatDisplayEnabled,
    onboardingState?.dashboardGuide,
  ]);

  if (isLoading) return <LoadingSkeleton />;

  return (
    <>
      <section className="dashboard-hero-section px-3 mt-3 mb-4">
        <div className="streak-container" ref={mainContentWrapperRef}>
          <CustomPopover
            isOpen={isTilesExplainerOpen}
            activeExplainer={activeExplainer}
            setActiveExplainer={setActiveExplainer}
            explainerSteps={POPOVER_STEPS}
            popoverContent={t(
              "tap_on_these_tiles_anytime_to_learn_more_about_hpr_and_gem",
            )}
            onFlowEnd={onFlowEnd}
          >
            <div
              className={`d-flex gap-3  ${
                isTilesExplainerOpen && "popover-node"
              } `}
              style={{ padding: "2px" }}
            >
              <CustomPopover
                isOpen={isHprExplainerOpen}
                activeExplainer={activeExplainer}
                setActiveExplainer={setActiveExplainer}
                explainerSteps={POPOVER_STEPS}
                popoverContent={t("this_is_your_hpr_balance_use_hpr_key")}
              >
                <div
                  className={`${isHprExplainerOpen && "popover-node"}`}
                  style={{
                    border: isTilesExplainerOpen ? "none" : undefined,
                  }}
                >
                  <HyperBalance
                    balance={hprWallet?.balance?.availableBalance}
                  />
                </div>
              </CustomPopover>
              <CustomPopover
                isOpen={isGemExplainerOpen}
                activeExplainer={activeExplainer}
                setActiveExplainer={setActiveExplainer}
                explainerSteps={POPOVER_STEPS}
                popoverContent={t("this_is_your_gem_balance_earn_gem_key")}
              >
                <div
                  className={`${isGemExplainerOpen && "popover-node"} pe-0`}
                  style={{ border: isTilesExplainerOpen ? "none" : undefined }}
                >
                  <GemBalance
                    balance={gemResponse?.balance}
                    expirationDate={gemResponse?.expiresAt}
                  />
                </div>
              </CustomPopover>
            </div>
          </CustomPopover>
          {!!streakStatus?.streakCount && (
            <StreakCount streakStatus={streakStatus} style={{ marginTop: 2 }} />
          )}
        </div>
        {hasWelcomeBonus && (
          <div className="bonus-container">
            <div>
              <Image src={ImageGiftIcon} width="13px" height="15px" />
              <span>{t("hundred_percent_welcome_insurance_bonus")}!</span>
            </div>
            <PrimaryActionButton
              variant="default"
              text={t("claim_bonus")}
              onClick={handleClaim}
              buttonClassName="claim-button"
              textClassName="claim-button-text"
            />
          </div>
        )}
      </section>
    </>
  );
}

export default DashboardHeroSection;
